import { useNavigate } from "react-router-dom";
import { MoreHorizontal, FileDown, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AcceptanceStatusBadge } from "./AcceptanceStatusBadge";
import { DeadlineBadge } from "./DeadlineBadge";
import { DTE_TYPE_LABELS } from "../types";
import type { ReceivedDocumentListItem, DteTypeCode } from "../types";

interface ReceivedDocumentTableProps {
  documents: ReceivedDocumentListItem[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onDownloadXml: (id: string) => void;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
}

function formatCurrency(value: string | null): string {
  if (!value) return "-";
  const num = parseFloat(value);
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ReceivedDocumentTable({
  documents,
  selectedIds,
  onSelectionChange,
  onDownloadXml,
  onAccept,
  onReject,
}: ReceivedDocumentTableProps) {
  const navigate = useNavigate();

  // Only allow selecting pending documents
  const selectableDocuments = documents.filter(
    (d) => d.acceptanceStatus === "pending",
  );

  const allSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((d) => selectedIds.has(d.id));

  const someSelected =
    selectableDocuments.some((d) => selectedIds.has(d.id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableDocuments.map((d) => d.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No hay documentos recibidos
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/50">
          <tr>
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                disabled={selectableDocuments.length === 0}
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Folio</th>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Proveedor
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium">Fecha</th>
            <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
            <th className="px-4 py-3 text-center text-sm font-medium">
              Estado
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium">Plazo</th>
            <th className="w-10 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {documents.map((doc) => {
            const isPending = doc.acceptanceStatus === "pending";
            return (
              <tr
                key={doc.id}
                className="hover:bg-muted/50 cursor-pointer group"
                onClick={() => navigate(`/facturacion/recepcion/${doc.id}`)}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(doc.id)}
                    onChange={() => toggleOne(doc.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    disabled={!isPending}
                  />
                </td>
                <td className="px-4 py-3 text-sm">
                  {DTE_TYPE_LABELS[
                    Number(doc.documentTypeCode) as DteTypeCode
                  ] ?? doc.documentType}
                </td>
                <td className="px-4 py-3 text-sm font-medium">{doc.folio}</td>
                <td className="px-4 py-3 text-sm">
                  <div>{doc.issuerName ?? "-"}</div>
                  <div className="text-muted-foreground text-xs">
                    {doc.issuerTaxId}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(doc.issueDate)}
                </td>
                <td className="px-4 py-3 text-sm text-right font-medium">
                  {formatCurrency(doc.totalAmount)}
                </td>
                <td className="px-4 py-3 text-center">
                  <AcceptanceStatusBadge status={doc.acceptanceStatus} />
                </td>
                <td className="px-4 py-3 text-center">
                  {isPending && (
                    <DeadlineBadge daysUntilDeadline={doc.daysUntilDeadline} />
                  )}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/facturacion/recepcion/${doc.id}`)
                        }
                      >
                        Ver detalle
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDownloadXml(doc.id)}>
                        <FileDown className="mr-2 h-4 w-4" />
                        Descargar XML
                      </DropdownMenuItem>
                      {isPending && (
                        <>
                          <DropdownMenuItem onClick={() => onAccept(doc.id)}>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Aceptar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onReject(doc.id)}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Rechazar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
